import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { ORDER_RESERVATION_STRATEGIES } from "constants/index";
import { Order, OrderReservationStrategy } from "types";

type Props = {
  open: boolean;
  orders: Order[];
  loading: boolean;
  onClose: () => void;
  onConfirm: (strategy: OrderReservationStrategy) => Promise<void>;
};

export default function OrderPreparationDialog({ open, orders, loading, onClose, onConfirm }: Props) {
  const [strategy, setStrategy] = useState<OrderReservationStrategy>(ORDER_RESERVATION_STRATEGIES.KEEP);

  useEffect(() => {
    if (open) setStrategy(ORDER_RESERVATION_STRATEGIES.KEEP);
  }, [open]);

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} disableEscapeKeyDown={loading} fullWidth maxWidth="md">
      <DialogTitle>Enviar pedidos a preparación</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2">
            Se enviarán {orders.length} pedido(s) a preparación. La opción elegida aplica a todo el lote.
          </Typography>
          <TableContainer sx={{ maxHeight: 300 }}>
            <Table size="small" stickyHeader aria-label="Pedidos a preparar">
              <TableHead>
                <TableRow>
                  <TableCell>Pedido</TableCell>
                  <TableCell>Doc. del cliente</TableCell>
                  <TableCell>Cliente asociado</TableCell>
                  <TableCell>Remito</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.map((order) => (
                  <TableRow key={order.id}>
                    <TableCell>{order.id}</TableCell>
                    <TableCell>{order.client_document || "-"}</TableCell>
                    <TableCell>{order.associated_client || "-"}</TableCell>
                    <TableCell>{order.numero_remito || "-"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <FormControl disabled={loading}>
            <FormLabel id="order-preparation-strategy-label">Ubicaciones del picking</FormLabel>
            <RadioGroup aria-labelledby="order-preparation-strategy-label" value={strategy}
              onChange={(event) => setStrategy(event.target.value as OrderReservationStrategy)}>
              <FormControlLabel value={ORDER_RESERVATION_STRATEGIES.KEEP} control={<Radio />} label="Respetar ubicación" />
              <FormControlLabel value={ORDER_RESERVATION_STRATEGIES.OPTIMIZE} control={<Radio />} label="Optimizar ubicación" />
            </RadioGroup>
          </FormControl>
          <Alert severity="info">
            {strategy === ORDER_RESERVATION_STRATEGIES.KEEP
              ? "Se conservan las ubicaciones actualmente reservadas."
              : "Se recalculan las ubicaciones priorizando menos posiciones y respetando FIFO y la presentación. Pueden reasignarse reservas de otros pedidos ingresados, incluso selecciones manuales. No se modifican pedidos detenidos, en preparación o preparados."}
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancelar</Button>
        <Button variant="contained" disabled={loading || orders.length === 0} onClick={() => { void onConfirm(strategy); }}>
          {loading ? "Enviando..." : "Confirmar envío"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
